import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Toolbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ToastContainer, toast } from "react-toastify";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { BASE_URL } from "../../global";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { useParams } from "react-router-dom";
import { styled } from "@mui/system";
import Tables from "../../utils/components/tables";
import html2pdf from "html2pdf.js";

const SingleEstimateReport = (jobsData) => {
  const jobDataArray = Object.values(jobsData);
  const jobData = jobDataArray?.[0];

  // Group titles based on index range
  const sectionGroups = [
    { title: "HV & LV Coils & M&P Unit", indices: [5, 6] },
    { title: "Fresh Transformer Oil of ISI", indices: [7] },
    {
      title: "Other small Parts",
      indices: [13, 14, 12, 17, 18, 16],
    },
    {
      title: "Labour Charges",
      indices: [20, 21, 22],
    },
  ];

  function extractLineItems(jobData) {
    const result = [];

    Object.keys(jobData).forEach((key) => {
      const match = key.match(/^name_(\d+)$/);
      if (match) {
        const index = match[1];

        result.push({
          serial: parseInt(index),
          name: jobData[`name_${index}`],
          id: jobData[`id_${index}`],
          quantity: parseFloat(jobData[`quantity_${index}`]) || 0,
          rate: parseFloat(jobData[`rate_${index}`]) || 0,
          amount: parseFloat(jobData[`amount_${index}`]) || 0,
          cgst: {
            rate: "9%" || 0,
            amount: parseFloat(jobData[`cgst_${index}`]) || 0,
          },
          sgst: {
            rate: "9%" || 0,
            amount: parseFloat(jobData[`sgst_${index}`]) || 0,
          },
          netAmount: parseFloat(jobData[`netAmount_${index}`]) || 0,
          transformerCapacity: jobData[`transformerCapacity_${index}`],
          circleId: jobData[`circleId_${index}`],
          unit: jobData[`unit_${index}`],
          ms: jobData[`missing_${index}`],
          dm: jobData[`damaged_${index}`],
          weight: jobData[`weight_${index}`],
        });
      }
    });

    return result.sort((a, b) => a.serial - b.serial);
  }

  const jobArray = extractLineItems(jobData);

  const getGroupItems = (group) =>
    jobArray.filter((item) => group.indices.includes(item.serial));

  const grossTotals = {
    amount: 0,
    cgst: 0,
    sgst: 0,
    netAmount: 0,
  };

  return (
    <TableContainer
      component={Paper}
      className="tableContainer"
      style={{ pageBreakInside: "avoid" }}
    >
      <Table className="table" id="table-to-pdf">
        <TableHead>
          <TableRow>
            <TableCell rowSpan={3} className="headerCell">
              P.O.S. No
            </TableCell>
            <TableCell rowSpan={3} className="headerCell">
              Name of Part / Material
            </TableCell>
            <TableCell colSpan={5} className="headerCell">
              Particulars based on Joint Inspection
            </TableCell>
            <TableCell colSpan={7} className="headerCell">
              Detailed Cost Estimate for Transformer
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell rowSpan={2} className="headerCell">
              MS
            </TableCell>
            <TableCell rowSpan={2} className="headerCell">
              DM
            </TableCell>
            <TableCell rowSpan={2} className="headerCell">
              Weight Each
            </TableCell>
            <TableCell rowSpan={2} className="headerCell">
              Quantity
            </TableCell>
            <TableCell rowSpan={2} className="headerCell">
              Unit
            </TableCell>
            <TableCell rowSpan={2} className="headerCell">
              Rate (Rs)
            </TableCell>
            <TableCell rowSpan={2} className="headerCell">
              Amount (Rs)
            </TableCell>
            <TableCell colSpan={2} className="headerCell">
              C GST
            </TableCell>
            <TableCell colSpan={2} className="headerCell">
              S GST
            </TableCell>
            <TableCell rowSpan={2} className="headerCell">
              Amount Incl All
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="headerCell">Rate</TableCell>
            <TableCell className="headerCell">Amount</TableCell>
            <TableCell className="headerCell">Rate</TableCell>
            <TableCell className="headerCell">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sectionGroups.map((group, sectionIndex) => {
            const groupItems = getGroupItems(group);
            const sectionTotal = {
              amount: 0,
              cgst: 0,
              sgst: 0,
              netAmount: 0,
            };

            return (
              <React.Fragment key={group.title}>
                <TableRow>
                  <TableCell
                    colSpan={14}
                    style={{
                      fontWeight: "bold",
                      textAlign: "left",
                      background: "#f2f2f2",
                    }}
                  >
                    {group.title}
                  </TableCell>
                </TableRow>
                {groupItems.map((item, idx) => {
                  sectionTotal.amount += item.amount;
                  sectionTotal.cgst += item.cgst.amount;
                  sectionTotal.sgst += item.sgst.amount;
                  sectionTotal.netAmount += item.netAmount;

                  return (
                    <TableRow key={idx}>
                      <TableCell className="bodyCell">{item.serial}</TableCell>
                      <TableCell className="bodyCell">{item.name}</TableCell>
                      <TableCell className="bodyCell">
                        {item.ms || ""}
                      </TableCell>
                      <TableCell className="bodyCell">
                        {item.dm || ""}
                      </TableCell>
                      <TableCell className="bodyCell">
                        {item.weight || ""}
                      </TableCell>
                      <TableCell className="bodyCell">
                        {item.quantity}
                      </TableCell>
                      <TableCell className="bodyCell">
                        {item.unit || ""}
                      </TableCell>
                      <TableCell className="bodyCell">{item.rate}</TableCell>
                      <TableCell className="bodyCell">{item.amount}</TableCell>
                      <TableCell className="bodyCell">
                        {item.cgst.rate}
                      </TableCell>
                      <TableCell className="bodyCell">
                        {item.cgst.amount}
                      </TableCell>
                      <TableCell className="bodyCell">
                        {item.sgst.rate}
                      </TableCell>
                      <TableCell className="bodyCell">
                        {item.sgst.amount}
                      </TableCell>
                      <TableCell className="bodyCell">
                        {item.netAmount}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Section subtotal */}
                <TableRow
                  style={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}
                >
                  <TableCell align="right"></TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right"></TableCell>

                  <TableCell>{sectionTotal.amount.toFixed(2)}</TableCell>
                  <TableCell />
                  <TableCell>{sectionTotal.cgst.toFixed(2)}</TableCell>
                  <TableCell />
                  <TableCell>{sectionTotal.sgst.toFixed(2)}</TableCell>
                  <TableCell>{sectionTotal.netAmount.toFixed(2)}</TableCell>
                </TableRow>

                {/* Safely update gross total without affecting the UI */}
                {(() => {
                  grossTotals.amount += sectionTotal.amount;
                  grossTotals.cgst += sectionTotal.cgst;
                  grossTotals.sgst += sectionTotal.sgst;
                  grossTotals.netAmount += sectionTotal.netAmount;
                  return null;
                })()}
              </React.Fragment>
            );
          })}

          {/* Final Gross Total */}
          <TableRow style={{ backgroundColor: "#c2c2c2", fontWeight: "bold" }}>
            <TableCell />
            <TableCell align="center" style={{ fontWeight: "bold" }}>
              A. Gross Total Cost of Repair
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />

            <TableCell>{grossTotals.amount.toFixed(2)}</TableCell>
            <TableCell />
            <TableCell>{grossTotals.cgst.toFixed(2)}</TableCell>
            <TableCell />
            <TableCell>{grossTotals.sgst.toFixed(2)}</TableCell>
            <TableCell>{grossTotals.netAmount.toFixed(2)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <style>
        {`
          .tableContainer {
            padding: 20px;
            max-width: 100%;
            overflow-x: auto;
            font-size: 8px;
          }

          .table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8px;
          }

          .headerCell, .bodyCell {
            border: 1px solid black;
            padding: 4px;
            text-align: center;
          }
        `}
      </style>
    </TableContainer>
  );
};

export default SingleEstimateReport;
